import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router';

import { paths } from '@/app/routes/paths';
import { authMe } from '@/features/auth/api/auth-api';
import { mapSessionUser, memberRolesAllowedForWebPortal, useAuthStore } from '@/store';

export function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useAuthStore((s) => s.logout);
  const [checkingSession, setCheckingSession] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      try {
        const raw = await authMe(controller.signal);
        if (controller.signal.aborted) {
          return;
        }
        const mapped = mapSessionUser(raw);
        if (!memberRolesAllowedForWebPortal(mapped.systemRoles)) {
          logout();
          setIsAuthorized(false);
          return;
        }
        setUser(mapped);
        setIsAuthorized(true);
      } catch {
        if (controller.signal.aborted) {
          return;
        }
        // Clear stale persisted user so LoginPage does not bounce back to a
        // protected route and re-trigger authMe in a loop (e.g. backend down).
        logout();
        setIsAuthorized(false);
      } finally {
        if (!controller.signal.aborted) {
          setCheckingSession(false);
        }
      }
    })();
    return () => {
      controller.abort();
    };
  }, [logout, setUser]);

  if (checkingSession) {
    return null;
  }

  // `logout()` clears `user` but does not reset `isAuthorized` from the earlier `authMe`.
  if (!isAuthorized || !user) {
    return <Navigate to={paths.login} replace />;
  }
  return children;
}
